import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SquareOutIcon from "@/assets/icons/square-out.svg?react";
import { BgtAvatar } from "@/components/BgtAvatar/BgtAvatar";
import { BgtPage } from "@/components/BgtLayout/BgtPage";
import { BgtPageContent } from "@/components/BgtLayout/BgtPageContent";
import BgtPageHeader from "@/components/BgtLayout/BgtPageHeader";
import { BgtTextStatistic } from "@/components/BgtStatistic/BgtTextStatistic";
import { BgtDataTable } from "@/components/BgtTable/BgtDataTable";
import { type Game, GameState } from "@/models";
import { getSettings } from "@/services/queries/settings";
import { getItemStateTranslationKey } from "@/utils/ItemStateUtils";
import { COMMON_LANGUAGE_CODES, getLanguageName, LANGUAGE_INDEPENDENT, LANGUAGE_NONE } from "@/utils/languageUtils";
import { RoundDecimal } from "@/utils/numberUtils";
import { SafeHttpUrl } from "@/utils/stringUtils";
import { EditableNumberCell } from "./-components/EditableNumberCell";
import { EditableSelectCell } from "./-components/EditableSelectCell";
import type { GamesFilterSearch } from "./-components/GamesFilters";
import { GamesToolbar } from "./-components/GamesToolbar";
import { mergeSearch, validateGamesSearch } from "./-components/gamesSearch";
import { overallRank } from "./-components/sortGames";
import { useGamesView } from "./-hooks/useGamesView";
import { useInlineGameUpdate } from "./-hooks/useInlineGameUpdate";

export const Route = createFileRoute("/games/table")({
	component: RouteComponent,
	validateSearch: validateGamesSearch,
});

function RouteComponent() {
	const { t, i18n } = useTranslation(["games", "game", "common"]);
	const search = Route.useSearch();
	const navigate = useNavigate();
	const { updateGame } = useInlineGameUpdate();
	const settingsQuery = useQuery(getSettings());
	const currency = settingsQuery.data?.currency;
	const dateFormat = settingsQuery.data?.dateFormat;

	const updateSearch = useCallback(
		(partial: Partial<GamesFilterSearch>, replace = false) => {
			navigate({ to: "/games/table", search: (prev) => mergeSearch(prev, partial), replace });
		},
		[navigate],
	);

	const view = useGamesView(search, updateSearch);
	const { isLoading, sorted: filtered } = view;

	const formatRange = useCallback((min: number | null, max: number | null, suffix = ""): string => {
		if (min == null && max == null) return "-";
		if (min != null && max != null) {
			const range = min === max ? `${min}` : `${min} - ${max}`;
			return suffix ? `${range} ${suffix}` : range;
		}
		const value = (min ?? max) as number;
		return suffix ? `${value} ${suffix}` : `${value}`;
	}, []);

	const totalPrice = useMemo(() => filtered.reduce((sum, game) => sum + (game.buyingPrice ?? 0), 0), [filtered]);
	const meanPrice = filtered.length > 0 ? RoundDecimal(totalPrice / filtered.length, 0.1) : 0;

	const stateEditItems = useMemo(
		() => Object.values(GameState).map((value) => ({ value, label: t(getItemStateTranslationKey(value, false)) })),
		[t],
	);
	const languageEditItems = useMemo(
		() => [
			{ value: LANGUAGE_NONE, label: t("game:language.none") },
			{ value: LANGUAGE_INDEPENDENT, label: t("game:language-independent") },
			...COMMON_LANGUAGE_CODES.map((code) => ({ value: code, label: getLanguageName(code, i18n.language) })),
		],
		[t, i18n.language],
	);

	const columns: ColumnDef<Game>[] = useMemo(
		() => [
			{
				accessorKey: "title",
				header: t("games:columns.title"),
				enableSorting: false,
				cell: ({ row }) => (
					<Link
						to="/games/$gameId"
						params={{ gameId: row.original.id }}
						className="flex items-center gap-2 hover:text-primary"
					>
						<BgtAvatar image={row.original.image} title={row.original.title} size="small" />
						<span>{row.original.title}</span>
					</Link>
				),
			},
			{
				id: "rank",
				// Unranked games sort last rather than first: a table sorted by rank
				// should not open on the games that have none.
				accessorFn: (game) => overallRank(game) ?? Number.MAX_SAFE_INTEGER,
				header: t("games:columns.rank"),
				cell: ({ row }) => overallRank(row.original) ?? "-",
				meta: { hideOnMobile: true },
			},
			{
				id: "players",
				accessorFn: (game) => game.minPlayers ?? game.maxPlayers ?? 0,
				header: t("games:columns.players"),
				cell: ({ row }) => formatRange(row.original.minPlayers, row.original.maxPlayers),
				meta: { hideOnMobile: true },
			},
			{
				id: "play-time",
				accessorFn: (game) => game.minPlayTime ?? game.maxPlayTime ?? 0,
				header: t("games:columns.play-time"),
				cell: ({ row }) => formatRange(row.original.minPlayTime, row.original.maxPlayTime, "min"),
				meta: { hideOnMobile: true },
			},
			{
				accessorKey: "weight",
				header: t("games:columns.weight"),
				cell: ({ row }) => (row.original.weight != null ? (RoundDecimal(row.original.weight, 0.1) ?? "-") : "-"),
				meta: { hideOnMobile: true },
			},
			{
				accessorKey: "rating",
				header: t("games:columns.rating"),
				cell: ({ row }) => (row.original.rating != null ? (RoundDecimal(row.original.rating, 0.1) ?? "-") : "-"),
				meta: { hideOnMobile: true },
			},
			{
				accessorKey: "language",
				header: t("games:columns.language"),
				cell: ({ row }) => (
					<EditableSelectCell
						value={row.original.language ?? LANGUAGE_NONE}
						items={languageEditItems}
						hasSearch
						onChange={(language) =>
							updateGame({ ...row.original, language: language === LANGUAGE_NONE ? null : language })
						}
					/>
				),
				meta: { hideOnMobile: true },
			},
			{
				accessorKey: "state",
				header: t("games:columns.state"),
				cell: ({ row }) => (
					<EditableSelectCell
						value={row.original.state}
						items={stateEditItems}
						onChange={(state) => updateGame({ ...row.original, state: state as GameState })}
					/>
				),
				meta: { hideOnMobile: true },
			},
			{
				accessorKey: "additionDate",
				header: t("games:columns.added"),
				cell: ({ row }) =>
					row.original.additionDate && dateFormat ? format(new Date(row.original.additionDate), dateFormat) : "-",
				meta: { hideOnMobile: true },
			},
			{
				accessorKey: "buyingPrice",
				header: t("games:columns.price"),
				cell: ({ row }) => (
					<EditableNumberCell
						value={row.original.buyingPrice}
						step={/* bgt-patch: whole units, not cents */ 1}
						min={0}
						prefix={currency}
						onChange={(buyingPrice) => updateGame({ ...row.original, buyingPrice })}
					/>
				),
				meta: { hideOnMobile: true },
			},
			{
				accessorKey: "shopUrl",
				header: t("games:columns.shop"),
				enableSorting: false,
				cell: ({ row }) => {
					const url = SafeHttpUrl(row.original.shopUrl);
					return url ? (
						<a
							href={url}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
						>
							<SquareOutIcon className="size-4" />
							{t("games:columns.shop")}
						</a>
					) : (
						"-"
					);
				},
			},
			{
				accessorKey: "bggId",
				header: t("games:columns.bgg"),
				enableSorting: false,
				cell: ({ row }) =>
					row.original.bggId ? (
						<a
							href={`https://boardgamegeek.com/boardgame/${row.original.bggId}`}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
						>
							<SquareOutIcon className="size-4" />
							{t("games:columns.bgg")}
						</a>
					) : (
						"-"
					),
			},
		],
		[t, currency, dateFormat, formatRange, updateGame, stateEditItems, languageEditItems],
	);

	const columnWidths: (string | null)[] = [null, null, null, null, null, null, "w-52", "w-52"];

	return (
		<BgtPage>
			<BgtPageHeader
				header={t("games:table.title")}
				backAction={() => navigate({ to: "/games", search: (prev) => prev })}
			/>
			<BgtPageContent>
				<GamesToolbar
					types={view.types}
					categories={view.categories}
					mechanics={view.mechanics}
					search={search}
					sortRules={view.sortRules}
					status={view.status}
					canSortByPlays={view.canSortByPlays}
					nameValue={view.nameValue}
					onNameChange={view.setNameValue}
					onSortChange={view.setSortRules}
					onSearchChange={updateSearch}
				/>
				<div className="grid grid-cols-2 lg:grid-cols-3 gap-3 xl:gap-6">
					<BgtTextStatistic title={t("games:table.total-games")} content={filtered.length} />
					<BgtTextStatistic
						title={t("games:table.total-price")}
						content={RoundDecimal(totalPrice, 0.1)}
						prefix={currency}
					/>
					<BgtTextStatistic title={t("games:table.mean-price")} content={meanPrice} prefix={currency} />
				</div>
				<BgtDataTable
					columns={columns}
					data={filtered}
					isLoading={isLoading}
					noDataMessage={t("games:table.empty")}
					widths={columnWidths}
				/>
			</BgtPageContent>
		</BgtPage>
	);
}
